import { EProvince } from '@if/core/types'
import {
  ControlledPostalCodeField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  useTranslatedOptionEnum,
} from '@if/mui'
import { useYearOptions } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { DefaultProgramConfig } from '@src/types/Constants'
import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  isCommercial?: boolean
  formControl: Control<TFieldValues>
  includeDuration?: boolean
  supportedProvinces?: EProvince[]
  disableEdit?: boolean
}

const AddressComponent = <TFieldValues extends FieldValues = FieldValues>({
  name,
  isCommercial = false,
  formControl,
  includeDuration = true,
  supportedProvinces = DefaultProgramConfig.supportedProvinces,
  disableEdit,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const provinceList = useTranslatedOptionEnum('EProvince', supportedProvinces, 'enum')
  const yearOptions = useYearOptions(15)

  return (
    <FieldGrid columns={{ xs: 1, sm: 2, lg: 3 }}>
      <ControlledTextField
        id="street"
        name={`${name}.street` as FieldPath<TFieldValues>}
        control={formControl}
        label={t('editCreditApplication.address.street')}
        slotProps={{ htmlInput: { maxLength: 100 } }}
        span="full"
        fullWidth
        disabled={disableEdit}
      />
      <ControlledTextField
        name={`${name}.civicNumber` as FieldPath<TFieldValues>}
        control={formControl}
        id="civicNumber"
        label={t('editCreditApplication.address.addressNumber')}
        fullWidth
        slotProps={{ htmlInput: { maxLength: 10 } }}
        disabled={disableEdit}
      />
      {!isCommercial && (
        <ControlledTextField
          name={`${name}.apartment` as FieldPath<TFieldValues>}
          id="apartment"
          control={formControl}
          label={t('editCreditApplication.address.apartment')}
          slotProps={{ htmlInput: { maxLength: 50 } }}
          fullWidth
          disabled={disableEdit}
        />
      )}
      {isCommercial && (
        <ControlledTextField
          name={`${name}.suite` as FieldPath<TFieldValues>}
          control={formControl}
          id="suite"
          label={t('editCreditApplication.address.suite')}
          slotProps={{ htmlInput: { maxLength: 10 } }}
          fullWidth
          disabled={disableEdit}
        />
      )}
      <ControlledTextField
        name={`${name}.city` as FieldPath<TFieldValues>}
        control={formControl}
        id="city"
        label={t('editCreditApplication.address.city')}
        slotProps={{ htmlInput: { maxLength: 50 } }}
        fullWidth
        disabled={disableEdit}
      />
      <ControlledPostalCodeField
        id="postalCode"
        name={`${name}.postalCode` as FieldPath<TFieldValues>}
        control={formControl}
        label={t('editCreditApplication.address.postalCode')}
        fullWidth
        disabled={disableEdit}
      />
      <ControlledSelect
        name={`${name}.stateIso` as FieldPath<TFieldValues>}
        id="province"
        control={formControl}
        options={provinceList}
        label={t('editCreditApplication.address.province')}
        disableEmptyOption
        fullWidth
        disabled={disableEdit}
      />
      {includeDuration && (
        <ControlledSelect
          id="addressSinceYears"
          name={`${name}.years` as FieldPath<TFieldValues>}
          control={formControl}
          label={t('editCreditApplication.address.since.years')}
          fullWidth
          options={yearOptions}
          disabled={disableEdit}
        />
      )}
    </FieldGrid>
  )
}

export default AddressComponent
